import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import './MenuPage.css';

// Utility function to resize images using Cloudinary transformations
const resizeImage = (url, width, height) => {
  if (!url) return '';
  const [baseUrl, params] = url.split('/upload/');
  return `${baseUrl}/upload/w_${width},h_${height},c_fill/${params}`;
};

// Image Modal component for showing larger images
const ImageModal = ({ isOpen, imageUrl, name, description, price, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-fade-in" onClick={onClose}>
      <div className="relative max-w-4xl w-full bg-white rounded-xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <button 
          onClick={onClose} 
          className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all"
          aria-label="Kapat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 h-64 md:h-96">
            <img 
              src={imageUrl} 
              alt={name} 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="w-full md:w-1/2 p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{name}</h2>
            <div className="bg-secondary/10 text-secondary px-3 py-1 rounded-full inline-block mb-4">
              {price !== null ? `${price.toFixed(2)} ₺` : 'Fiyat bilgisi yok'}
            </div>
            <p className="text-gray-600 mb-6">{description}</p>
            <button className="bg-secondary hover:bg-primary text-white py-2 px-6 rounded-lg transition-colors shadow-md">
              Sipariş Ver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function MenuPage() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState('Tümü');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('default');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const itemsPerPage = 25;
  
  // Observer for lazy loading
  const imgObserver = useRef(null);
  const observerSupported = useRef('IntersectionObserver' in window);

  // Modal handling functions
  const openModal = useCallback((item) => {
    setSelectedItem(item);
    setModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.body.style.overflow = ''; // Re-enable scrolling
  }, []);

  // Add keyboard event listener for ESC key to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && modalOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [modalOpen, closeModal]);

  useEffect(() => {
    // Setup Intersection Observer for lazy loading images
    if (observerSupported.current) {
      imgObserver.current = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute('data-src');
            if (src) {
              img.src = src;
              img.classList.add('fade-in');
              observer.unobserve(img);
            }
          }
        });
      }, { threshold: 0.1, rootMargin: '100px' });
    }

    // Fetch menu data
    setLoading(true);
    
    // Get token from localStorage for authentication
    const token = localStorage.getItem('token');
    
    // Updated API endpoint to match backend route and include authentication
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/prods/getproducts`)
      .then(response => {
        // Extract items array from response based on updated API structure
        const data = Array.isArray(response.data.items) ? response.data.items.map(item => ({
          ...item,
          price: typeof item.price === 'number' ? item.price : null,
          imageUrl: resizeImage(item.imageUrl, 400, 300),
        })) : [];
        setMenuItems(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching menu:', error);
        if (error.response?.status === 401) {
          setError('Menü görüntülemek için giriş yapmalısınız.');
        } else {
          setError('Menü yüklenirken bir hata oluştu.');
        }
        setLoading(false);
      });

    return () => {
      if (imgObserver.current) {
        imgObserver.current.disconnect();
      }
    };
  }, []);

  // Lazy load images when not using IntersectionObserver
  useEffect(() => {
    if (!observerSupported.current && !loading) {
      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => {
        img.src = img.getAttribute('data-src');
        img.addEventListener('load', () => img.classList.add('fade-in'));
      });
    }
  }, [loading, currentPage, activeCategory]);

  // Get all categories from menu items - memoized
  const categories = useMemo(() => {
    const categoriesList = [...new Set(menuItems.map(item => item.category))];
    return ['Tümü', ...categoriesList];
  }, [menuItems]);

  // Filter and sort menu items - memoized
  const filteredItems = useMemo(() => {
    let filtered = [...menuItems];
    
    // Filter by category
    if (activeCategory !== 'Tümü') {
      filtered = filtered.filter(item => item.category === activeCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) || 
        item.description.toLowerCase().includes(term)
      );
    }
    
    // Sort items
    switch(sortOption) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Default sort (e.g. by popularity or default order)
        break;
    }
    
    return filtered;
  }, [menuItems, activeCategory, searchTerm, sortOption]);

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Handle image reference - optimized approach
  const handleImageRef = useCallback((el) => {
    if (el && observerSupported.current && imgObserver.current) {
      imgObserver.current.observe(el);
    }
  }, []);

  // Handle page change
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setActiveCategory('Tümü');
    setCurrentPage(1);
  }, []);

  // Placeholder image for lazy loading - SVG data URI optimized for size
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300' fill='%23f3f4f6'%3E%3Crect width='400' height='300'/%3E%3C/svg%3E";

  // Pagination controls - memoized to prevent unnecessary re-renders
  const PaginationControls = useMemo(() => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, idx) => idx + 1);
      } else if (currentPage <= 3) {
        return [1, 2, 3, 4, 5];
      } else if (currentPage >= totalPages - 2) {
        return Array.from({ length: 5 }, (_, idx) => totalPages - 4 + idx);
      } else {
        return Array.from({ length: 5 }, (_, idx) => currentPage - 2 + idx);
      }
    };

    return (
      <div className="flex justify-center mt-8 sm:mt-12">
        <div className="flex flex-wrap justify-center gap-2">
          <button 
            onClick={() => handlePageChange(currentPage - 1)} 
            disabled={currentPage === 1}
            className={`px-3 sm:px-4 py-2 rounded-md ${
              currentPage === 1 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-white text-primary hover:bg-gray-100 shadow'
            }`}
            aria-label="Önceki sayfa"
          >
            &laquo; <span className="hidden sm:inline">Önceki</span>
          </button>
          
          {getPageNumbers().map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`w-10 h-10 flex items-center justify-center rounded-md ${
                currentPage === pageNum
                  ? 'bg-secondary text-white shadow-md transform scale-110'
                  : 'bg-white text-primary hover:bg-gray-100 shadow'
              }`}
              aria-label={`Sayfa ${pageNum}`}
              aria-current={currentPage === pageNum ? "page" : undefined}
            >
              {pageNum}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className={`px-3 sm:px-4 py-2 rounded-md ${
              currentPage === totalPages 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-white text-primary hover:bg-gray-100 shadow'
            }`}
            aria-label="Sonraki sayfa"
          >
            <span className="hidden sm:inline">Sonraki</span> &raquo;
          </button>
        </div>
      </div>
    );
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <div className="bg-light min-h-screen pt-16 sm:pt-20 md:pt-24 pb-12 px-3 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Hero section with animated background */}
        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl mb-6 sm:mb-10 bg-gradient-to-r from-primary to-secondary shadow-xl">
          <div className="coffee-animation"></div>
          <div className="relative z-10 p-6 sm:p-8 md:p-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-bold text-white mb-3 sm:mb-4 animate-fade-in">
              Menümüz
            </h1>
            <p className="text-light/90 text-base sm:text-lg md:text-xl max-w-2xl animate-slide-up">
              En seçkin ve lezzetli kahve çeşitlerimiz, tatlılarımız ve atıştırmalıklarımız ile sizi ağırlamaktan mutluluk duyarız.
            </p>
          </div>
        </div>

        {/* Filters and search */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 animate-fade-in">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Menüde ara..."
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Menüde arama yap"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent bg-white transition-all"
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Sıralama seçeneği"
            >
              <option value="default">Varsayılan Sıralama</option>
              <option value="price-asc">Fiyat (Artan)</option>
              <option value="price-desc">Fiyat (Azalan)</option>
              <option value="name">İsme Göre (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Category tabs - scrollable for mobile */}
        <div className="mb-6 sm:mb-8 overflow-x-auto hide-scrollbar animate-fade-in">
          <div className="flex space-x-2 py-1 min-w-max">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setActiveCategory(category);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-secondary text-white shadow-md transform -translate-y-1'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Error and loading states */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p>{error}</p>
          </div>
        )}
        
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-coffee">☕</div>
            <p className="ml-3 text-lg text-primary">Menü yükleniyor...</p>
          </div>
        )}

        {/* Menu items grid - updated with click handler */}
        {!loading && (
          <>
            {paginatedItems.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600">Arama sonucu bulunamadı.</p>
                <button 
                  onClick={handleResetFilters}
                  className="mt-4 px-6 py-2 bg-secondary text-white rounded-lg hover:bg-primary transition-all"
                >
                  Tüm menüyü göster
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                {paginatedItems.map((item, index) => (
                  <div 
                    key={item.id || index} 
                    className="menu-card group bg-white rounded-xl shadow-md overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl cursor-pointer"
                    onClick={() => openModal(item)}
                  >
                    <div className="relative h-40 xs:h-44 sm:h-48 overflow-hidden">
                      <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                      <img
                        ref={handleImageRef}
                        src={placeholderImage}
                        data-src={item.imageUrl || placeholderImage}
                        alt={item.name || 'Ürün resmi'}
                        className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                        loading="lazy" // Native lazy loading as backup
                      />
                      {item.isNew && (
                        <div className="absolute top-2 right-2 bg-accent text-dark px-2 py-1 rounded-full text-xs font-bold animate-bounce">
                          Yeni!
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <p className="text-white font-medium">{item.category}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold text-dark line-clamp-1">{item.name}</h3>
                        <span className="bg-light text-secondary px-2 py-1 rounded font-bold whitespace-nowrap ml-1">
                          {item.price !== null ? `${item.price.toFixed(2)} ₺` : 'Belirtilmemiş'}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{item.description}</p>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg 
                              key={star} 
                              xmlns="http://www.w3.org/2000/svg" 
                              className={`h-3 w-3 sm:h-4 sm:w-4 ${star <= (item.rating || 4) ? 'text-accent' : 'text-gray-300'}`}
                              viewBox="0 0 20 20" 
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                          <span className="text-xs text-gray-500 ml-1">
                            ({item.reviewCount || Math.floor(Math.random() * 50) + 5})
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {PaginationControls}
          </>
        )}
        
        {/* Modal for displaying larger image and details */}
        <ImageModal 
          isOpen={modalOpen} 
          imageUrl={selectedItem?.imageUrl} 
          name={selectedItem?.name} 
          description={selectedItem?.description}
          price={selectedItem?.price}
          onClose={closeModal}
        />
      </div>
    </div>
  );
}

export default MenuPage;
